/**
 * LoginGate — 로그인하지 않으면 화면이 아예 그려지지 않습니다.
 *
 * 🔴 이 화면이 지키는 것은 **회의·통화 녹음의 전사문과 원본 음원**입니다.
 *    누가 언제 무슨 말을 했는지가 그대로 들어 있습니다. 그래서 "화면만 가리고
 *    데이터는 흐르는" 방식으로는 부족합니다 — 실제 차단은 항상 서버(`auth.py`)가
 *    하며, 이 컴포넌트는 그 상태를 사람이 이해할 수 있게 보여 줄 뿐입니다.
 *
 * 흐름
 * ----
 *   `/api/auth/me` 로 지금 상태를 묻습니다
 *     → 인증 꺼짐            : 그대로 통과
 *     → 이 컴퓨터에서 직접    : 그대로 통과 (`local: true`)
 *     → 로그인됨             : 그대로 통과
 *     → 아니면               : Google 로그인 버튼만 보여 줍니다
 */
import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react'
import { AudioWaveform, LogOut, ShieldCheck } from 'lucide-react'

import { api } from '@/lib/api'
import type { AuthUser } from '@/types/api'
import { cn } from '@/lib/utils'
// 🔴 로컬에서 Google 로그인이 막히실 때 외부 주소로 건너가시는 링크입니다.
import { PublicUrlNotice } from './PublicUrlNotice'

/* Google Identity Services 가 전역에 심는 객체 — 타입만 최소로 선언합니다. */
declare global {
  interface Window {
    google?: {
      accounts: {
        id: {
          initialize: (o: {
            client_id: string
            callback: (r: { credential: string }) => void
            auto_select?: boolean
            cancel_on_tap_outside?: boolean
          }) => void
          renderButton: (el: HTMLElement, o: Record<string, unknown>) => void
        }
      }
    }
  }
}

const GIS_SRC = 'https://accounts.google.com/gsi/client'

/** 🔴 좁은 화면(360·412px)에서 터치 대상 최소 44×44px 을 보장합니다. */
const TAP = 'max-sm:min-h-11 max-sm:min-w-11'

/** GIS 스크립트를 한 번만 싣습니다. 두 번 부르면 같은 약속을 돌려줍니다. */
let gisPromise: Promise<void> | null = null
function loadGis(): Promise<void> {
  if (gisPromise) return gisPromise
  gisPromise = new Promise((resolve, reject) => {
    if (window.google?.accounts?.id) return resolve()
    const s = document.createElement('script')
    s.src = GIS_SRC
    s.async = true
    s.defer = true
    s.onload = () => resolve()
    // 🔴 조용히 실패하면 "버튼이 안 뜬다" 로만 보이고 원인을 알 수 없습니다.
    s.onerror = () =>
      reject(new Error('Google 로그인 스크립트를 불러오지 못했습니다 (네트워크를 확인하시길 권합니다)'))
    document.head.appendChild(s)
  })
  return gisPromise
}

type State =
  | { phase: 'checking' }
  | { phase: 'open' } //                       인증이 꺼진 실행
  | { phase: 'in'; user: AuthUser | null; local: boolean }
  | { phase: 'out'; clientId: string }
  | { phase: 'error'; message: string }

export function LoginGate({ children }: { children: ReactNode }) {
  const [state, setState] = useState<State>({ phase: 'checking' })
  const [denied, setDenied] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const btnRef = useRef<HTMLDivElement | null>(null)

  const check = useCallback(async () => {
    try {
      const me = await api.authMe()
      if (!me.enabled) return setState({ phase: 'open' })
      if (me.authenticated) {
        return setState({ phase: 'in', user: me.user, local: Boolean(me.local) })
      }
      const cfg = await api.authConfig()
      if (!cfg.google_client_id) {
        return setState({
          phase: 'error',
          message:
            '서버에 Google 클라이언트 ID 가 설정되어 있지 않습니다 ' +
            '(config.json → auth.google_client_id).',
        })
      }
      setState({ phase: 'out', clientId: cfg.google_client_id })
    } catch (e) {
      setState({ phase: 'error', message: e instanceof Error ? e.message : String(e) })
    }
  }, [])

  useEffect(() => {
    void check()
  }, [check])

  useEffect(() => {
    if (state.phase !== 'out') return
    let cancelled = false

    void loadGis()
      .then(() => {
        if (cancelled || !btnRef.current || !window.google) return
        window.google.accounts.id.initialize({
          client_id: state.clientId,
          auto_select: false,
          cancel_on_tap_outside: true,
          callback: async (res) => {
            setBusy(true)
            setDenied(null)
            try {
              await api.authGoogle(res.credential)
              await check()
            } catch {
              // 401·403 의 사유를 그대로 노출하지 않습니다 — 어떤 계정이 허용
              // 목록에 있는지 알려 주는 셈이 됩니다. 기록은 서버가 남깁니다.
              setDenied('이 계정은 접근이 허용되지 않았습니다. 허용된 계정으로 로그인해 주시길 권합니다.')
            } finally {
              setBusy(false)
            }
          },
        })
        // 🔴 폭을 고정값으로 두면 360px 화면에서 카드 여백을 빼고 나면 버튼이
        //    옆으로 넘칩니다. 담을 자리의 폭을 재서 GIS 허용 범위로 맞춥니다.
        const fit = Math.round(Math.max(200, Math.min(400, btnRef.current.clientWidth || 280)))
        window.google.accounts.id.renderButton(btnRef.current, {
          theme: 'filled_black',
          size: 'large',
          text: 'signin_with',
          shape: 'rectangular',
          locale: 'ko',
          width: fit,
        })
      })
      .catch((e: Error) => {
        if (!cancelled) setState({ phase: 'error', message: e.message })
      })

    return () => {
      cancelled = true
    }
  }, [state, check])

  if (state.phase === 'checking') {
    return (
      <Shell>
        <p className="text-sm text-muted-foreground" role="status" aria-live="polite">
          접근 권한을 확인하는 중입니다…
        </p>
      </Shell>
    )
  }

  if (state.phase === 'error') {
    return (
      <Shell>
        <p className="text-sm text-destructive" role="alert">
          {state.message}
        </p>
        <button
          type="button"
          data-action="login-retry"
          onClick={() => {
            setState({ phase: 'checking' })
            void check()
          }}
          className={cn(
            'mt-4 rounded-md border border-border px-4 py-2 text-sm whitespace-nowrap',
            'transition-colors hover:bg-accent focus-visible:outline-none focus-visible:ring-2',
            'focus-visible:ring-ring',
            TAP,
          )}
        >
          다시 시도
        </button>
        <PublicUrlNotice publicUrl="https://vox.instapp.run" />
      </Shell>
    )
  }

  if (state.phase === 'out') {
    return (
      <Shell>
        <p className="text-sm text-muted-foreground">
          이 서비스는 <b className="text-foreground">녹음 원본과 전사문</b>을 다룹니다.
          <br />
          허용된 계정으로만 열람하실 수 있습니다.
        </p>
        <div data-role="login-google-button" ref={btnRef} className="mt-6 flex justify-center" />
        {busy ? (
          <p className="mt-3 text-xs text-muted-foreground" role="status" aria-live="polite">
            확인하는 중입니다…
          </p>
        ) : null}
        {denied ? (
          <p className="mt-3 text-xs text-warning" role="alert">
            {denied}
          </p>
        ) : null}
        <PublicUrlNotice publicUrl="https://vox.instapp.run" />
      </Shell>
    )
  }

  return <>{children}</>
}

/** 로그인 화면의 틀 — 본 화면이 뜨기 전이므로 자체적으로 완결되어야 합니다. */
function Shell({ children }: { children: ReactNode }) {
  return (
    <div
      data-component="login-gate"
      className="flex min-h-screen items-center justify-center bg-background p-4 sm:p-6"
    >
      <div className="w-full max-w-md rounded-xl border border-border bg-card p-6 text-center sm:p-8">
        <div className="flex flex-nowrap items-center justify-center gap-2">
          <AudioWaveform className="size-6 shrink-0 text-primary" aria-hidden />
          <ShieldCheck className="size-6 shrink-0 text-primary" aria-hidden />
        </div>
        <h1 className="mt-3 text-lg font-semibold whitespace-nowrap">voxctx</h1>
        <p className="mt-1 text-xs text-muted-foreground whitespace-nowrap">
          음원 · 전사 · 에이전트 컨텍스트
        </p>
        <div className="mt-6">{children}</div>
      </div>
    </div>
  )
}

/**
 * 누구로 들어와 있는지 + 나가기.
 *
 * 🔴 인증이 꺼져 있으면 아무것도 그리지 않습니다. 로컬 직접 접속이면 그 사실을
 *    **분명히 적습니다** — 로그인한 것으로 오해하시면 안 됩니다.
 */
export function AuthBadge() {
  const [me, setMe] = useState<{ user: AuthUser | null; local: boolean } | null>(null)

  useEffect(() => {
    void api
      .authMe()
      .then((m) => {
        if (!m.enabled || !m.authenticated) return setMe(null)
        setMe({ user: m.user, local: Boolean(m.local) })
      })
      .catch(() => setMe(null))
  }, [])

  if (!me) return null

  if (me.local) {
    return (
      <div
        data-component="auth-badge"
        data-state="local"
        className="flex flex-nowrap items-center gap-1.5 rounded-md border border-border bg-secondary px-2 py-1 text-[11px] text-muted-foreground whitespace-nowrap"
        title="이 컴퓨터에서 직접 열었기 때문에 로그인 없이 보고 계십니다. 외부(vox.instapp.run)에서는 로그인이 필요합니다."
      >
        <ShieldCheck className="size-3.5 shrink-0 text-success" aria-hidden />
        이 컴퓨터에서 직접
      </div>
    )
  }

  return (
    <div
      data-component="auth-badge"
      data-state="signed-in"
      className="flex min-w-0 flex-nowrap items-center gap-1.5 rounded-md border border-border bg-secondary px-2 py-1 text-[11px]"
    >
      {me.user?.picture ? (
        <img src={me.user.picture} alt="" className="size-4 shrink-0 rounded-full" />
      ) : (
        <ShieldCheck className="size-3.5 shrink-0 text-success" aria-hidden />
      )}
      <span className="min-w-0 truncate text-muted-foreground" title={me.user?.email ?? ''}>
        {me.user?.email ?? '로그인됨'}
      </span>
      <button
        type="button"
        data-action="logout"
        title="로그아웃"
        aria-label="로그아웃"
        onClick={async () => {
          await api.authLogout()
          window.location.reload()
        }}
        className={cn(
          'shrink-0 rounded p-0.5 text-muted-foreground transition-colors',
          'hover:bg-accent hover:text-foreground focus-visible:outline-none',
          'focus-visible:ring-2 focus-visible:ring-ring',
        )}
      >
        <LogOut className="size-3.5" aria-hidden />
      </button>
    </div>
  )
}
